import { Events, useEventBus, DialogEvent, useDialogBus } from '@/helpers/event-bus'
import type { DefaultObject } from '@/types'
import { useI18n } from 'vue-i18n'
import { useOfficeStore } from '@/stores/office'
import { computed } from 'vue'
import { useValidator, type ValidationSchema } from '@/validation/form-validator'
import type { NavigationGuardNext } from 'vue-router'
import { scrollToElement } from '@/helpers/shared'
import { useOfficeCalculator } from '@/stores/officeCenter/officeCalculator'
import type { UpdateDataType } from '@/components/partials/display/VersionUpdate.vue'

export interface OfficeValidationConfig {
  schema: ValidationSchema
  fieldSections?: Record<string, string> | (() => Record<string, string>) // field name -> section selector
  sectionErrors?: Record<string, string> | (() => Record<string, string>) // section selector -> error i18n key
}

export default {
  useOfficePageBase(validationConfig?: OfficeValidationConfig) {
    const { t } = useI18n()
    const eventBus = useEventBus()
    const dialogBus = useDialogBus()
    const officeStore = useOfficeStore()
    const office: DefaultObject | null = computed(() => officeStore.getOffice)
    const officeMeta: DefaultObject | null = computed(() => officeStore.getMeta)
    const officeName = computed(() => officeStore.getMeta?.title)

    const calculator = useOfficeCalculator()
    const availableUpdates = computed(() => officeStore.getAvailableUpdates)

    const initCalculator = function () {
      calculator.initOffice(office.value?.data)
    }

    function findCurrentSectionIndex(): number {
      const sections = document.querySelectorAll('section.core-container')
      const viewportMiddle = window.innerHeight / 2
      let currentIndex = 0

      sections.forEach((el, index) => {
        const box = el.getBoundingClientRect()
        if (box.y < viewportMiddle) {
          currentIndex = index
        }
      })

      return currentIndex
    }

    const validator = useValidator()
    const validationSchema = validationConfig?.schema ?? {}

    async function checkFormIsDirty(
      next: NavigationGuardNext,
      onSaveData: { data: Record<string, unknown> | undefined }
    ): Promise<void> {
      if (validator.isDirty) {
        const confirmed = await checkLeaveDialog()
        if (confirmed) {
          const success = await saveOffice(onSaveData.data)
          if (success) {
            next()
            return
          } else {
            next(false)
            return
          }
        }
      }
      next()
    }

    async function checkLeaveDialog(): Promise<boolean> {
      return new Promise<boolean>((resolve) => {
        dialogBus.emit(DialogEvent.SHOW_CONFIRM, {
          title: t('general.navigate_from.title'),
          text: t('general.navigate_from.message'),
          confirm: t('general.navigate_from.yes'),
          cancel: t('general.navigate_from.no'),
        })
        dialogBus.on(DialogEvent.DONE, async (confirmed: boolean) => {
          dialogBus.off(DialogEvent.DONE)
          return resolve(confirmed)
        })
      })
    }

    async function validateFormAndShowErrors(): Promise<boolean> {
      if (!validator.validate()) {
        const fieldSectionsConfig = validationConfig?.fieldSections
        const sectionErrorsConfig = validationConfig?.sectionErrors

        // Support both static objects and functions for dynamic sections
        const fieldSections = typeof fieldSectionsConfig === 'function' ? fieldSectionsConfig() : fieldSectionsConfig
        const sectionErrors = typeof sectionErrorsConfig === 'function' ? sectionErrorsConfig() : sectionErrorsConfig

        if (fieldSections && sectionErrors) {
          // Find the first failed field and its section
          const errorKeys = Object.keys(validator.errors.value)
          for (const key of errorKeys) {
            // Clean array keys (e.g., 'usageCorrections.0.manualArea' -> 'usageCorrections[].manualArea')
            const cleanKey = key.replace(/\.\d+\./g, '[].')
            const sectionId = fieldSections[cleanKey]
            if (sectionId) {
              const errorKey = sectionErrors[sectionId]
              if (errorKey) {
                eventBus.emit(Events.SHOW_ERROR, { key: errorKey })
              }
              const section = document.querySelector(sectionId)
              if (section) {
                scrollToElement(section)
              }
              break
            }
          }
        }
        return false
      }
      return true
    }

    async function onNextStepClick(fromSection?: Element): Promise<void> {
      const sections = Array.from(document.querySelectorAll('section.core-container'))
      let currentIndex: number
      if (fromSection) {
        currentIndex = sections.indexOf(fromSection)
      } else {
        currentIndex = findCurrentSectionIndex()
      }
      const nextSection = sections[currentIndex + 1]
      if (nextSection) {
        scrollToElement(nextSection)
      }
    }

    async function saveOffice(data: Record<string, unknown> | undefined): Promise<boolean> {
      if (!data) return false
      if (!(await validateFormAndShowErrors())) {
        return false
      }
      const success = await officeStore.updateOffice(data)
      if (success) {
        officeStore.triggerClientSave()
        return true
      } else {
        // handle error
        return false
      }
    }

    async function updateVersion(update: UpdateDataType) {
      if (!(await validateFormAndShowErrors())) {
        return false
      }
      officeStore.versionUpdate(update.version)
      initCalculator()
    }

    async function ignoreVersion(update: UpdateDataType) {
      if (!(await validateFormAndShowErrors())) {
        return false
      }
      officeStore.versionIgnore(update.version)
      return true
    }

    return {
      office,
      initCalculator,
      officeMeta,
      officeName,
      validator,
      validationSchema,
      calculator,
      checkFormIsDirty,
      saveOffice,
      onNextStepClick,
      availableUpdates,
      updateVersion,
      ignoreVersion,
    }
  },
}
